import { useCallback } from 'react'
import { LaunchDarkly } from '@truepill/tpos-types'
import config from 'config'
import { useFlags as useLDFlags, LDProvider, useLDClient, useLDClientError } from 'launchdarkly-react-client-sdk'
import type { LDContext, LDFlagSet, ProviderConfig } from 'launchdarkly-react-client-sdk'
import type { ChildProps } from 'types'

let lastContext: LDContext | null = null

export const useFlag = (flag: LaunchDarkly.FeatureFlags) => {
  const flags = useLDFlags()
  return flag in flags && flags[flag]
}

export const useFlags = ({ ...flags }: { [key: string]: LaunchDarkly.FeatureFlags }) => {
  const allFlags = useLDFlags()
  return Object.fromEntries(Object.entries(flags).map(([key, flag]) => [key, allFlags[flag]]))
}

export const useClient = () => {
  const client = useLDClient()
  const error = useLDClientError()

  const ldIdentify = useCallback(
    (context: LDContext, hash?: string, onDone?: (err: Error | null, flags: LDFlagSet | null) => void) => {
      if (!client) {
        return
      }

      const currentContext = lastContext || {}

      // Merge parameters, with custom objects being merged deeply
      const mergedContext: LDContext = {
        ...currentContext,
        ...context,
        custom: {
          ...currentContext.custom,
          ...context.custom,
        },
      }

      // Store the merged data for future use
      lastContext = mergedContext

      client.identify(mergedContext, hash, onDone)
    },
    [client],
  )
  // Do not expose Launch Darkly client to avoid direct calls to client.identify that would override the context
  return {
    ldReady: Boolean(client),
    error,
    ldIdentify,
    ldGetContext: () => client?.getContext(),
  }
}

const { clientSideID } = config.launchDarkly
const providerConfig: ProviderConfig = {
  clientSideID,
  options: {
    bootstrap: 'localStorage',
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  flags: {
    [LaunchDarkly.FeatureFlags.ALERT_CLINICAL_OUTREACH_TO_MD_TRIAGE]: false,
    [LaunchDarkly.FeatureFlags.ALLOW_ERX_REJECTION_FROM_CUSTOMER_UNASSIGNED]: false,
    [LaunchDarkly.FeatureFlags.ALLOW_ERX_REJECTION_FROM_PATIENT_MATCH]: false,
    [LaunchDarkly.FeatureFlags.ALLOW_LINEAR_BARCODE_SCANNING]: false,
    [LaunchDarkly.FeatureFlags.BYPASS_ORG_CHECK_FOR_INVENTORY]: false,
    [LaunchDarkly.FeatureFlags.COVER_MY_MEDS_PRIOR_AUTH]: false,
    [LaunchDarkly.FeatureFlags.DECOUPLE_CRITICAL_NOTES]: false,
    [LaunchDarkly.FeatureFlags.DISPLAY_NEW_COPAY_CHECK_BUTTON]: false,
    [LaunchDarkly.FeatureFlags.DISPLAY_STRIPE_ID_IN_UI]: false,
    [LaunchDarkly.FeatureFlags.DISPENSING_LOCATION_REQUIRES_NY_LICENSE]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_AUTO_SHIPPING_LABEL_PRINTING]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_BULK_ACTIONS]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_FIFO_LOCATION_FALLBACK]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_LOT_ACCURACY_MODAL_GTIN_SCANNING]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_NEW_PRESCRIPTION_CREATE_V2]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_ORDER_SPLITTING]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_PACKING_MATERIALS_ACCURACY_SCANNING]: false,
    [LaunchDarkly.FeatureFlags.PA_STATUS_CHECK_BUTTON]: undefined,
    [LaunchDarkly.FeatureFlags.PA_FAX_PRESCRIBER_BUTTON]: false,
    [LaunchDarkly.FeatureFlags.ENABLE_REQUEST_CLARIFICATION_BUTTON]: false,
    [LaunchDarkly.FeatureFlags.PV1_INTERFACE_CONSISTENCY]: false,
    [LaunchDarkly.FeatureFlags.PV1_HIDE_WEIGHT_BY_CUSTOMER]: false,
    [LaunchDarkly.FeatureFlags.REQUIRE_USER_TO_OUTBOUND_SCAN_DURING_PACKING]: false,
    [LaunchDarkly.FeatureFlags.TEMP_ADD_OTC_RX_LABEL_TO_QUEUES]: false,
    [LaunchDarkly.FeatureFlags.TEMP_BLOCK_CONFIRM_PV_ON_LOADING_CRITICAL_NOTES]: false,
    [LaunchDarkly.FeatureFlags.TEMP_ENABLE_PA_EXPIRES_ON]: false,
    [LaunchDarkly.FeatureFlags.TEMP_DUPLICATE_RX_MODAL]: false,
    [LaunchDarkly.FeatureFlags.TEMP_DUR_TOKEN_MATCHING]: false,
    [LaunchDarkly.FeatureFlags.TEMP_ENHANCE_PATIENT_SEARCH]: false,
    [LaunchDarkly.FeatureFlags.TEMP_ENABLE_SATURDAY_DELIVERY_ELIGIBILITY_FILTER]: false,
    [LaunchDarkly.FeatureFlags.TEMP_ENABLE_WAREHOUSE_SHELF_LOCATIONS_SORT]: false,
    [LaunchDarkly.FeatureFlags.TEMP_FILTERS_RESET_PAGINATION]: false,
    [LaunchDarkly.FeatureFlags.TEMP_GET_FULFILLMENT_ORDER_HINT]: false,
    [LaunchDarkly.FeatureFlags.TEMP_HIDE_CONSENT_ON_PV1]: false,
    [LaunchDarkly.FeatureFlags.TEMP_HIDE_REFILLS_WRITEEN_FIELD_RX_REVIEW]: false,
    [LaunchDarkly.FeatureFlags.TEMP_HIGHLIGHT_PV1_DIFFERENCES]: false,
    [LaunchDarkly.FeatureFlags.TEMP_OTC_ADJUDICATION_HIDE_RX_COMPONENT]: false,
    [LaunchDarkly.FeatureFlags.TEMP_SUPPORT_CSA_NOTES]: false,
    [LaunchDarkly.FeatureFlags.TPOS_DASHBOARD_DESIGN_V2]: false,
    [LaunchDarkly.FeatureFlags.TPOS_PV1_DESIGN_V2]: false,
    [LaunchDarkly.FeatureFlags.TEMP_USE_GRAPHQL_QUERY_IN_COMPONENT_FILE]: false,
    [LaunchDarkly.FeatureFlags.TEMP_UPDATE_UNAVAILABLE_TO_ORDER_REJECTION_CODE]: false,
    [LaunchDarkly.FeatureFlags.USE_COPAY_PAYMENT_VALIDATION]: false,
    [LaunchDarkly.FeatureFlags.USE_COPAY_VALIDATION_ON_RESOLVE_PATIENT_OUTREACH_TRIAGE]: false,
    [LaunchDarkly.FeatureFlags.USE_SIMPLIFIED_MEDICATION_SEARCH]: false,
    [LaunchDarkly.FeatureFlags.COMPLETE_COPAY_REQUEST_FILL_MUTATION_RETURN_ONLY_ID]: false,
    [LaunchDarkly.FeatureFlags.TEMP_PRIOR_AUTHORIZATION_QUEUE_NEWEST_TO_OLDEST]: false,
    [LaunchDarkly.FeatureFlags.TEMP_HIGHLIGHT_FIRST_FILL]: false,
    [LaunchDarkly.FeatureFlags.PA_RENEWAL_QUEUE]: false,
    [LaunchDarkly.FeatureFlags.TPOS_DASHBOARD_MULTISELECTORS]: false,
    [LaunchDarkly.FeatureFlags.USE_RX_CLARIFICATION_STATUS]: false,
    [LaunchDarkly.FeatureFlags.TEMP_INITIATE_PA_STATIC_BUTTON]: false,
    [LaunchDarkly.FeatureFlags.TEMP_INSURANCE_PLAN_TYPE_FIELD]: false,
    [LaunchDarkly.FeatureFlags.CREATE_ROUTING_RULES]: false,
    [LaunchDarkly.FeatureFlags.SHOW_ALERT_WHEN_MISSING_DEA_VALUE]: false,
    [LaunchDarkly.FeatureFlags.SITE_PERFORMANCE_STATUS]: 'normal',
    [LaunchDarkly.FeatureFlags.TEMP_CANCEL_OTC_BUTTON]: false,
  },
}

const LaunchDarklyProvider = ({ children }: ChildProps): JSX.Element => (
  <LDProvider {...providerConfig}>{children}</LDProvider>
)
export default LaunchDarklyProvider
